// Location Risk Service (Phase 4)
// Calculates dynamic risk profiles for physical locations (rooms, departments, buildings)
// Based on historical infection rates, current patients, environmental conditions, staffing, incidents, and devices

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDoc, 
  getDocs,
  query, 
  where,
  orderBy,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { departmentService, roomService, bedService, personnelService, buildingService, floorService, locationService, organizationService } from './firebaseService';

export class LocationRiskService {
  constructor() {
    this.profilesCollection = collection(db, 'location_risk_profiles');
  }

  /**
   * Calculate comprehensive location risk profile
   * @param {string} locationId - Location entity ID
   * @param {string} locationType - "room" | "department" | "floor" | "building" | "location" | "organization"
   * @returns {Promise<Object>} Location risk profile with multiplier
   * 
   * Supports all location types:
   * - 'room', 'department': Direct calculation from data
   * - 'floor', 'building', 'location', 'organization': Hierarchical aggregation from child locations
   */
  async calculateLocationRisk(locationId, locationType = 'room') {
    try {
      // For hierarchical locations (floor, building, location, organization), aggregate from children
      const isHierarchical = ['floor', 'floors', 'building', 'buildings', 'location', 'locations', 'organization', 'organizations'].includes(locationType);
      
      if (isHierarchical) {
        return await this.aggregateRiskFromChildren(locationId, locationType);
      }
      
      // For direct locations (room, department), calculate from data
      const historical = await this.getHistoricalInfectionRate(locationId, locationType);
      const patients = await this.getCurrentPatientRisk(locationId, locationType);
      const environmental = await this.getEnvironmentalConditions(locationId, locationType);
      const staffing = await this.getStaffingAdequacy(locationId, locationType);
      const incidents = await this.getRecentIncidents(locationId, locationType);
      const devices = await this.getDeviceRisk(locationId, locationType);
      
      // Calculate individual multipliers
      const multipliers = {
        historical: this.calculateHistoricalMultiplier(historical),
        patient: this.calculatePatientMultiplier(patients),
        environmental: this.calculateEnvironmentalMultiplier(environmental),
        staffing: this.calculateStaffingMultiplier(staffing),
        incidents: this.calculateIncidentsMultiplier(incidents),
        devices: this.calculateDeviceMultiplier(devices)
      };
      
      // Overall location risk multiplier (product of all)
      const locationMultiplier = Object.values(multipliers).reduce((a, b) => a * b, 1.0);
      
      // Cap at 3.0 as per spec
      const cappedMultiplier = Math.min(locationMultiplier, 3.0);
      
      // Determine risk level
      const riskLevel = this.determineRiskLevel(cappedMultiplier);
      
      // Get location name
      const locationName = await this.getLocationName(locationId, locationType);
      
      // Get endemic organisms (placeholder - would query from HAI data)
      const endemicOrganisms = await this.getEndemicOrganisms(locationId, locationType);
      
      // Generate recommendations
      const recommendations = this.generateRecommendations(cappedMultiplier, multipliers);
      
      // Build risk profile
      const riskProfile = {
        location_id: locationId,
        location_type: locationType,
        location_name: locationName,
        
        // Base risk
        base_risk_score: cappedMultiplier,
        
        // Individual multipliers for calculation display
        multipliers: multipliers,
        
        // Risk factors with multipliers
        risk_factors: {
          historical_infection_rate: {
            ...historical,
            multiplier: multipliers.historical
          },
          current_patient_risk: {
            ...patients,
            multiplier: multipliers.patient
          },
          environmental_conditions: {
            ...environmental,
            multiplier: multipliers.environmental
          },
          staffing_adequacy: {
            ...staffing,
            multiplier: multipliers.staffing
          },
          recent_incidents: {
            ...incidents,
            multiplier: multipliers.incidents
          },
          device_risk: {
            ...devices,
            multiplier: multipliers.devices
          }
        },
        
        // Calculated multiplier
        location_risk_multiplier: cappedMultiplier,
        
        // Risk classification
        risk_level: riskLevel.level,
        risk_level_rationale: riskLevel.rationale,
        
        // Biological entities
        endemic_organisms: endemicOrganisms,
        
        // Mitigation
        active_interventions: [],
        recommended_precautions: recommendations,
        
        // Metadata
        last_calculated: serverTimestamp(),
        calculation_frequency: 'hourly',
        next_calculation: new Date(Date.now() + 60 * 60 * 1000), // 1 hour
        
        updated_at: serverTimestamp()
      };
      
      // Save to database
      await this.saveLocationRiskProfile(locationId, riskProfile);
      
      return {
        location_id: locationId,
        location_type: locationType,
        location_name: locationName,
        location_risk_multiplier: cappedMultiplier,
        risk_level: riskLevel.level,
        risk_level_rationale: riskLevel.rationale,
        risk_factors: riskProfile.risk_factors,
        multipliers: multipliers,
        recommendations: recommendations
      };
    } catch (error) {
      console.error('Error calculating location risk:', error);
      throw error;
    }
  }

  /**
   * Get historical infection rate for location
   */
  async getHistoricalInfectionRate(locationId, locationType) {
    // TODO: Query from HAI/compliance data (last 30 days)
    // For now, return default structure
    return {
      rate_per_1000_patient_days: 5.0, // National benchmark default
      trend: 'stable',
      cases_last_30_days: 0,
      patient_days_last_30_days: 0
    };
  }

  /**
   * Get current patient risk aggregation
   * Supports all location types: direct calculation for rooms/departments, aggregation for hierarchical locations
   */
  async getCurrentPatientRisk(locationId, locationType) {
    try {
      // Get patients in this location
      let patientCount = 0;
      let highRiskCount = 0;
      let immunocompromisedCount = 0;
      let deviceDependentCount = 0;
      let isolationCount = 0;
      
      if (locationType === 'room' || locationType === 'rooms') {
        try {
          // Get beds in room, then patients
          const beds = await bedService.getWhere('roomId', '==', locationId);
          patientCount = beds.filter(b => b.status === 'occupied' || b.status === 'occupied').length;
          // TODO: Query patient risk profiles for detailed counts
        } catch (error) {
          console.warn('Error getting beds for room:', error);
        }
      } else if (locationType === 'department' || locationType === 'departments') {
        try {
          // Get rooms in department, then beds, then patients
          const rooms = await roomService.getWhere('departmentId', '==', locationId);
          const bedPromises = rooms.map(r => 
            bedService.getWhere('roomId', '==', r.id).catch(err => {
              console.warn(`Error getting beds for room ${r.id}:`, err);
              return [];
            })
          );
          const bedArrays = await Promise.all(bedPromises);
          const allBeds = bedArrays.flat();
          patientCount = allBeds.filter(b => b.status === 'occupied' || b.status === 'occupied').length;
          // TODO: Query patient risk profiles for detailed counts
        } catch (error) {
          console.warn('Error getting rooms or beds for department:', error);
        }
      } else if (locationType === 'floor' || locationType === 'floors') {
        try {
          // Get departments in floor, then aggregate
          const departments = await departmentService.getWhere('floorId', '==', locationId);
          const roomPromises = departments.map(d => 
            roomService.getWhere('departmentId', '==', d.id).catch(err => {
              console.warn(`Error getting rooms for department ${d.id}:`, err);
              return [];
            })
          );
          const roomArrays = await Promise.all(roomPromises);
          const allRooms = roomArrays.flat();
          const bedPromises = allRooms.map(r => 
            bedService.getWhere('roomId', '==', r.id).catch(err => {
              console.warn(`Error getting beds for room ${r.id}:`, err);
              return [];
            })
          );
          const bedArrays = await Promise.all(bedPromises);
          const allBeds = bedArrays.flat();
          patientCount = allBeds.filter(b => b.status === 'occupied' || b.status === 'occupied').length;
        } catch (error) {
          console.warn('Error getting departments, rooms, or beds for floor:', error);
        }
      } else if (locationType === 'building' || locationType === 'buildings') {
        try {
          // Get floors in building, then aggregate
          const floors = await floorService.getWhere('buildingId', '==', locationId);
          const deptPromises = floors.map(f => 
            departmentService.getWhere('floorId', '==', f.id).catch(err => {
              console.warn(`Error getting departments for floor ${f.id}:`, err);
              return [];
            })
          );
          const deptArrays = await Promise.all(deptPromises);
          const allDepartments = deptArrays.flat();
          const roomPromises = allDepartments.map(d => 
            roomService.getWhere('departmentId', '==', d.id).catch(err => {
              console.warn(`Error getting rooms for department ${d.id}:`, err);
              return [];
            })
          );
          const roomArrays = await Promise.all(roomPromises);
          const allRooms = roomArrays.flat();
          const bedPromises = allRooms.map(r => 
            bedService.getWhere('roomId', '==', r.id).catch(err => {
              console.warn(`Error getting beds for room ${r.id}:`, err);
              return [];
            })
          );
          const bedArrays = await Promise.all(bedPromises);
          const allBeds = bedArrays.flat();
          patientCount = allBeds.filter(b => b.status === 'occupied' || b.status === 'occupied').length;
        } catch (error) {
          console.warn('Error getting floors, departments, rooms, or beds for building:', error);
        }
      } else if (locationType === 'location' || locationType === 'locations') {
        try {
          // Get buildings in location, then aggregate
          const buildings = await buildingService.getWhere('locationId', '==', locationId);
          const floorPromises = buildings.map(b => 
            floorService.getWhere('buildingId', '==', b.id).catch(err => {
              console.warn(`Error getting floors for building ${b.id}:`, err);
              return [];
            })
          );
          const floorArrays = await Promise.all(floorPromises);
          const allFloors = floorArrays.flat();
          const deptPromises = allFloors.map(f => 
            departmentService.getWhere('floorId', '==', f.id).catch(err => {
              console.warn(`Error getting departments for floor ${f.id}:`, err);
              return [];
            })
          );
          const deptArrays = await Promise.all(deptPromises);
          const allDepartments = deptArrays.flat();
          const roomPromises = allDepartments.map(d => 
            roomService.getWhere('departmentId', '==', d.id).catch(err => {
              console.warn(`Error getting rooms for department ${d.id}:`, err);
              return [];
            })
          );
          const roomArrays = await Promise.all(roomPromises);
          const allRooms = roomArrays.flat();
          const bedPromises = allRooms.map(r => 
            bedService.getWhere('roomId', '==', r.id).catch(err => {
              console.warn(`Error getting beds for room ${r.id}:`, err);
              return [];
            })
          );
          const bedArrays = await Promise.all(bedPromises);
          const allBeds = bedArrays.flat();
          patientCount = allBeds.filter(b => b.status === 'occupied' || b.status === 'occupied').length;
        } catch (error) {
          console.warn('Error getting buildings, floors, departments, rooms, or beds for location:', error);
        }
      } else if (locationType === 'organization' || locationType === 'organizations') {
        try {
          // Get locations in organization, then aggregate
          const locations = await locationService.getWhere('organizationId', '==', locationId);
          const buildingPromises = locations.map(l => 
            buildingService.getWhere('locationId', '==', l.id).catch(err => {
              console.warn(`Error getting buildings for location ${l.id}:`, err);
              return [];
            })
          );
          const buildingArrays = await Promise.all(buildingPromises);
          const allBuildings = buildingArrays.flat();
          const floorPromises = allBuildings.map(b => 
            floorService.getWhere('buildingId', '==', b.id).catch(err => {
              console.warn(`Error getting floors for building ${b.id}:`, err);
              return [];
            })
          );
          const floorArrays = await Promise.all(floorPromises);
          const allFloors = floorArrays.flat();
          const deptPromises = allFloors.map(f => 
            departmentService.getWhere('floorId', '==', f.id).catch(err => {
              console.warn(`Error getting departments for floor ${f.id}:`, err);
              return [];
            })
          );
          const deptArrays = await Promise.all(deptPromises);
          const allDepartments = deptArrays.flat();
          const roomPromises = allDepartments.map(d => 
            roomService.getWhere('departmentId', '==', d.id).catch(err => {
              console.warn(`Error getting rooms for department ${d.id}:`, err);
              return [];
            })
          );
          const roomArrays = await Promise.all(roomPromises);
          const allRooms = roomArrays.flat();
          const bedPromises = allRooms.map(r => 
            bedService.getWhere('roomId', '==', r.id).catch(err => {
              console.warn(`Error getting beds for room ${r.id}:`, err);
              return [];
            })
          );
          const bedArrays = await Promise.all(bedPromises);
          const allBeds = bedArrays.flat();
          patientCount = allBeds.filter(b => b.status === 'occupied' || b.status === 'occupied').length;
        } catch (error) {
          console.warn('Error getting locations, buildings, floors, departments, rooms, or beds for organization:', error);
        }
      }
      
      // Calculate average risk multiplier (placeholder)
      const averageRiskMultiplier = patientCount > 0 ? 
        Math.min(1.0 + (highRiskCount * 0.3) + (immunocompromisedCount * 0.2), 2.0) : 
        1.0;
      
      return {
        total_patients: patientCount,
        high_risk_patients: highRiskCount,
        immunocompromised_count: immunocompromisedCount,
        device_dependent_count: deviceDependentCount,
        isolation_count: isolationCount,
        average_risk_multiplier: averageRiskMultiplier
      };
    } catch (error) {
      console.error('Error getting current patient risk:', error);
      return {
        total_patients: 0,
        high_risk_patients: 0,
        immunocompromised_count: 0,
        device_dependent_count: 0,
        isolation_count: 0,
        average_risk_multiplier: 1.0
      };
    }
  }

  /**
   * Get environmental conditions
   */
  async getEnvironmentalConditions(locationId, locationType) {
    // TODO: Query from environmental_assessments collection
    // For now, return default structure
    return {
      ventilation_status: 'adequate',
      humidity_level: 50,
      temperature_level: 22,
      cleaning_compliance: 95 // 0-100%
    };
  }

  /**
   * Get staffing adequacy
   * Supports all location types: direct calculation for rooms/departments, aggregation for hierarchical locations
   */
  async getStaffingAdequacy(locationId, locationType) {
    try {
      let currentStaff = 0;
      let requiredStaff = 8; // Default requirement
      
      if (locationType === 'department' || locationType === 'departments') {
        try {
          const personnel = await personnelService.getWhere('departmentId', '==', locationId);
          currentStaff = personnel.filter(p => p.status === 'active').length;
          // Estimate required staff based on department type
          requiredStaff = currentStaff > 0 ? Math.max(currentStaff, 8) : 8;
        } catch (error) {
          console.warn('Error getting personnel for department:', error);
        }
      } else if (locationType === 'room' || locationType === 'rooms') {
        // Get department for room
        try {
          const room = await roomService.getById(locationId);
          if (room?.departmentId) {
            const personnel = await personnelService.getWhere('departmentId', '==', room.departmentId);
            currentStaff = personnel.filter(p => p.status === 'active').length;
            requiredStaff = Math.max(2, currentStaff); // Minimum 2 per room
          }
        } catch (error) {
          console.warn('Error getting room or personnel:', error);
        }
      } else if (locationType === 'floor' || locationType === 'floors') {
        // Aggregate from departments in floor
        try {
          const departments = await departmentService.getWhere('floorId', '==', locationId);
          const personnelPromises = departments.map(d => 
            personnelService.getWhere('departmentId', '==', d.id).catch(err => {
              console.warn(`Error getting personnel for department ${d.id}:`, err);
              return [];
            })
          );
          const personnelArrays = await Promise.all(personnelPromises);
          const allPersonnel = personnelArrays.flat();
          currentStaff = allPersonnel.filter(p => p.status === 'active').length;
          requiredStaff = Math.max(currentStaff, departments.length * 8); // Minimum 8 per department
        } catch (error) {
          console.warn('Error getting departments or personnel for floor:', error);
        }
      } else if (locationType === 'building' || locationType === 'buildings') {
        // Aggregate from floors in building
        try {
          const floors = await floorService.getWhere('buildingId', '==', locationId);
          const deptPromises = floors.map(f => 
            departmentService.getWhere('floorId', '==', f.id).catch(err => {
              console.warn(`Error getting departments for floor ${f.id}:`, err);
              return [];
            })
          );
          const deptArrays = await Promise.all(deptPromises);
          const allDepartments = deptArrays.flat();
          const personnelPromises = allDepartments.map(d => 
            personnelService.getWhere('departmentId', '==', d.id).catch(err => {
              console.warn(`Error getting personnel for department ${d.id}:`, err);
              return [];
            })
          );
          const personnelArrays = await Promise.all(personnelPromises);
          const allPersonnel = personnelArrays.flat();
          currentStaff = allPersonnel.filter(p => p.status === 'active').length;
          requiredStaff = Math.max(currentStaff, allDepartments.length * 8); // Minimum 8 per department
        } catch (error) {
          console.warn('Error getting floors, departments, or personnel for building:', error);
        }
      } else if (locationType === 'location' || locationType === 'locations') {
        // Aggregate from buildings in location
        try {
          const buildings = await buildingService.getWhere('locationId', '==', locationId);
          const floorPromises = buildings.map(b => 
            floorService.getWhere('buildingId', '==', b.id).catch(err => {
              console.warn(`Error getting floors for building ${b.id}:`, err);
              return [];
            })
          );
          const floorArrays = await Promise.all(floorPromises);
          const allFloors = floorArrays.flat();
          const deptPromises = allFloors.map(f => 
            departmentService.getWhere('floorId', '==', f.id).catch(err => {
              console.warn(`Error getting departments for floor ${f.id}:`, err);
              return [];
            })
          );
          const deptArrays = await Promise.all(deptPromises);
          const allDepartments = deptArrays.flat();
          const personnelPromises = allDepartments.map(d => 
            personnelService.getWhere('departmentId', '==', d.id).catch(err => {
              console.warn(`Error getting personnel for department ${d.id}:`, err);
              return [];
            })
          );
          const personnelArrays = await Promise.all(personnelPromises);
          const allPersonnel = personnelArrays.flat();
          currentStaff = allPersonnel.filter(p => p.status === 'active').length;
          requiredStaff = Math.max(currentStaff, allDepartments.length * 8); // Minimum 8 per department
        } catch (error) {
          console.warn('Error getting buildings, floors, departments, or personnel for location:', error);
        }
      } else if (locationType === 'organization' || locationType === 'organizations') {
        // Aggregate from locations in organization
        try {
          const locations = await locationService.getWhere('organizationId', '==', locationId);
          const buildingPromises = locations.map(l => 
            buildingService.getWhere('locationId', '==', l.id).catch(err => {
              console.warn(`Error getting buildings for location ${l.id}:`, err);
              return [];
            })
          );
          const buildingArrays = await Promise.all(buildingPromises);
          const allBuildings = buildingArrays.flat();
          const floorPromises = allBuildings.map(b => 
            floorService.getWhere('buildingId', '==', b.id).catch(err => {
              console.warn(`Error getting floors for building ${b.id}:`, err);
              return [];
            })
          );
          const floorArrays = await Promise.all(floorPromises);
          const allFloors = floorArrays.flat();
          const deptPromises = allFloors.map(f => 
            departmentService.getWhere('floorId', '==', f.id).catch(err => {
              console.warn(`Error getting departments for floor ${f.id}:`, err);
              return [];
            })
          );
          const deptArrays = await Promise.all(deptPromises);
          const allDepartments = deptArrays.flat();
          const personnelPromises = allDepartments.map(d => 
            personnelService.getWhere('departmentId', '==', d.id).catch(err => {
              console.warn(`Error getting personnel for department ${d.id}:`, err);
              return [];
            })
          );
          const personnelArrays = await Promise.all(personnelPromises);
          const allPersonnel = personnelArrays.flat();
          currentStaff = allPersonnel.filter(p => p.status === 'active').length;
          requiredStaff = Math.max(currentStaff, allDepartments.length * 8); // Minimum 8 per department
        } catch (error) {
          console.warn('Error getting locations, buildings, floors, departments, or personnel for organization:', error);
        }
      }
      
      const ratio = requiredStaff > 0 ? currentStaff / requiredStaff : 0;
      
      return {
        current_staff_count: currentStaff,
        required_staff_count: requiredStaff,
        ratio: ratio
      };
    } catch (error) {
      console.error('Error getting staffing adequacy:', error);
      return {
        current_staff_count: 0,
        required_staff_count: 8,
        ratio: 0
      };
    }
  }

  /**
   * Get recent incidents (last 7 days)
   */
  async getRecentIncidents(locationId, locationType) {
    // TODO: Query from deviations, HAI cases, compliance failures
    // For now, return default structure
    return {
      hai_cases: 0,
      near_misses: 0,
      compliance_failures: 0
    };
  }

  /**
   * Get device density risk
   * Supports all location types: direct calculation for rooms/departments, aggregation for hierarchical locations
   */
  async getDeviceRisk(locationId, locationType) {
    try {
      let totalBeds = 0;
      let invasiveDevices = 0;
      
      if (locationType === 'room' || locationType === 'rooms') {
        try {
          const beds = await bedService.getWhere('roomId', '==', locationId);
          totalBeds = beds.length;
          // TODO: Query devices from equipment collection
          invasiveDevices = 0; // Placeholder
        } catch (error) {
          console.warn('Error getting beds for room:', error);
        }
      } else if (locationType === 'department' || locationType === 'departments') {
        try {
          const rooms = await roomService.getWhere('departmentId', '==', locationId);
          const bedPromises = rooms.map(r => 
            bedService.getWhere('roomId', '==', r.id).catch(err => {
              console.warn(`Error getting beds for room ${r.id}:`, err);
              return [];
            })
          );
          const bedArrays = await Promise.all(bedPromises);
          totalBeds = bedArrays.flat().length;
          // TODO: Query devices from equipment collection
          invasiveDevices = 0; // Placeholder
        } catch (error) {
          console.warn('Error getting rooms or beds for department:', error);
        }
      } else if (locationType === 'floor' || locationType === 'floors') {
        try {
          // Aggregate from departments in floor
          const departments = await departmentService.getWhere('floorId', '==', locationId);
          const roomPromises = departments.map(d => 
            roomService.getWhere('departmentId', '==', d.id).catch(err => {
              console.warn(`Error getting rooms for department ${d.id}:`, err);
              return [];
            })
          );
          const roomArrays = await Promise.all(roomPromises);
          const allRooms = roomArrays.flat();
          const bedPromises = allRooms.map(r => 
            bedService.getWhere('roomId', '==', r.id).catch(err => {
              console.warn(`Error getting beds for room ${r.id}:`, err);
              return [];
            })
          );
          const bedArrays = await Promise.all(bedPromises);
          totalBeds = bedArrays.flat().length;
          // TODO: Query devices from equipment collection
          invasiveDevices = 0; // Placeholder
        } catch (error) {
          console.warn('Error getting departments, rooms, or beds for floor:', error);
        }
      } else if (locationType === 'building' || locationType === 'buildings') {
        try {
          // Aggregate from floors in building
          const floors = await floorService.getWhere('buildingId', '==', locationId);
          const deptPromises = floors.map(f => 
            departmentService.getWhere('floorId', '==', f.id).catch(err => {
              console.warn(`Error getting departments for floor ${f.id}:`, err);
              return [];
            })
          );
          const deptArrays = await Promise.all(deptPromises);
          const allDepartments = deptArrays.flat();
          const roomPromises = allDepartments.map(d => 
            roomService.getWhere('departmentId', '==', d.id).catch(err => {
              console.warn(`Error getting rooms for department ${d.id}:`, err);
              return [];
            })
          );
          const roomArrays = await Promise.all(roomPromises);
          const allRooms = roomArrays.flat();
          const bedPromises = allRooms.map(r => 
            bedService.getWhere('roomId', '==', r.id).catch(err => {
              console.warn(`Error getting beds for room ${r.id}:`, err);
              return [];
            })
          );
          const bedArrays = await Promise.all(bedPromises);
          totalBeds = bedArrays.flat().length;
          // TODO: Query devices from equipment collection
          invasiveDevices = 0; // Placeholder
        } catch (error) {
          console.warn('Error getting floors, departments, rooms, or beds for building:', error);
        }
      } else if (locationType === 'location' || locationType === 'locations') {
        try {
          // Aggregate from buildings in location
          const buildings = await buildingService.getWhere('locationId', '==', locationId);
          const floorPromises = buildings.map(b => 
            floorService.getWhere('buildingId', '==', b.id).catch(err => {
              console.warn(`Error getting floors for building ${b.id}:`, err);
              return [];
            })
          );
          const floorArrays = await Promise.all(floorPromises);
          const allFloors = floorArrays.flat();
          const deptPromises = allFloors.map(f => 
            departmentService.getWhere('floorId', '==', f.id).catch(err => {
              console.warn(`Error getting departments for floor ${f.id}:`, err);
              return [];
            })
          );
          const deptArrays = await Promise.all(deptPromises);
          const allDepartments = deptArrays.flat();
          const roomPromises = allDepartments.map(d => 
            roomService.getWhere('departmentId', '==', d.id).catch(err => {
              console.warn(`Error getting rooms for department ${d.id}:`, err);
              return [];
            })
          );
          const roomArrays = await Promise.all(roomPromises);
          const allRooms = roomArrays.flat();
          const bedPromises = allRooms.map(r => 
            bedService.getWhere('roomId', '==', r.id).catch(err => {
              console.warn(`Error getting beds for room ${r.id}:`, err);
              return [];
            })
          );
          const bedArrays = await Promise.all(bedPromises);
          totalBeds = bedArrays.flat().length;
          // TODO: Query devices from equipment collection
          invasiveDevices = 0; // Placeholder
        } catch (error) {
          console.warn('Error getting buildings, floors, departments, rooms, or beds for location:', error);
        }
      } else if (locationType === 'organization' || locationType === 'organizations') {
        try {
          // Aggregate from locations in organization
          const locations = await locationService.getWhere('organizationId', '==', locationId);
          const buildingPromises = locations.map(l => 
            buildingService.getWhere('locationId', '==', l.id).catch(err => {
              console.warn(`Error getting buildings for location ${l.id}:`, err);
              return [];
            })
          );
          const buildingArrays = await Promise.all(buildingPromises);
          const allBuildings = buildingArrays.flat();
          const floorPromises = allBuildings.map(b => 
            floorService.getWhere('buildingId', '==', b.id).catch(err => {
              console.warn(`Error getting floors for building ${b.id}:`, err);
              return [];
            })
          );
          const floorArrays = await Promise.all(floorPromises);
          const allFloors = floorArrays.flat();
          const deptPromises = allFloors.map(f => 
            departmentService.getWhere('floorId', '==', f.id).catch(err => {
              console.warn(`Error getting departments for floor ${f.id}:`, err);
              return [];
            })
          );
          const deptArrays = await Promise.all(deptPromises);
          const allDepartments = deptArrays.flat();
          const roomPromises = allDepartments.map(d => 
            roomService.getWhere('departmentId', '==', d.id).catch(err => {
              console.warn(`Error getting rooms for department ${d.id}:`, err);
              return [];
            })
          );
          const roomArrays = await Promise.all(roomPromises);
          const allRooms = roomArrays.flat();
          const bedPromises = allRooms.map(r => 
            bedService.getWhere('roomId', '==', r.id).catch(err => {
              console.warn(`Error getting beds for room ${r.id}:`, err);
              return [];
            })
          );
          const bedArrays = await Promise.all(bedPromises);
          totalBeds = bedArrays.flat().length;
          // TODO: Query devices from equipment collection
          invasiveDevices = 0; // Placeholder
        } catch (error) {
          console.warn('Error getting locations, buildings, floors, departments, rooms, or beds for organization:', error);
        }
      }
      
      const devicesPerBed = totalBeds > 0 ? invasiveDevices / totalBeds : 0;
      
      return {
        invasive_devices_per_bed: devicesPerBed,
        device_infection_risk: Math.min(devicesPerBed * 0.1, 0.5) // Cap at 0.5
      };
    } catch (error) {
      console.error('Error getting device risk:', error);
      return {
        invasive_devices_per_bed: 0,
        device_infection_risk: 0
      };
    }
  }

  /**
   * Calculate historical infection rate multiplier
   */
  calculateHistoricalMultiplier(historical) {
    const baseMultiplier = 1.0;
    const rate = historical.rate_per_1000_patient_days;
    const benchmark = 5.0; // National benchmark: 5 per 1000 patient days
    
    // If infection rate is increasing → higher multiplier
    if (historical.trend === 'increasing') {
      if (rate > benchmark * 2) return 2.0;
      if (rate > benchmark * 1.5) return 1.6;
      if (rate > benchmark) return 1.3;
    }
    
    // If infection rate is decreasing → lower multiplier
    if (historical.trend === 'decreasing') {
      if (rate < benchmark * 0.5) return 0.8;
      if (rate < benchmark * 0.7) return 0.9;
    }
    
    // Stable or at benchmark
    if (rate > benchmark) return 1.2;
    if (rate < benchmark * 0.5) return 0.8;
    
    return baseMultiplier;
  }

  /**
   * Calculate patient risk multiplier
   */
  calculatePatientMultiplier(patients) {
    if (patients.total_patients === 0) return 1.0;
    
    // Use average risk multiplier from patient profiles
    return Math.min(patients.average_risk_multiplier || 1.0, 2.0);
  }

  /**
   * Calculate environmental multiplier
   */
  calculateEnvironmentalMultiplier(environmental) {
    let multiplier = 1.0;
    
    // Ventilation
    if (environmental.ventilation_status === 'poor') multiplier *= 1.3;
    if (environmental.ventilation_status === 'optimal') multiplier *= 0.9;
    
    // Cleaning compliance
    if (environmental.cleaning_compliance < 80) multiplier *= 1.2;
    if (environmental.cleaning_compliance >= 95) multiplier *= 0.95;
    
    // Humidity and temperature (ideal: 40-60% humidity, 20-24°C)
    if (environmental.humidity_level < 30 || environmental.humidity_level > 70) multiplier *= 1.1;
    if (environmental.temperature_level < 18 || environmental.temperature_level > 26) multiplier *= 1.1;
    
    return Math.min(multiplier, 1.5); // Cap at 1.5
  }

  /**
   * Calculate staffing multiplier
   */
  calculateStaffingMultiplier(staffing) {
    const ratio = staffing.ratio;
    
    if (ratio < 0.6) return 2.0;      // Critically understaffed
    if (ratio < 0.8) return 1.5;      // Understaffed
    if (ratio < 1.0) return 1.2;      // Slightly understaffed
    if (ratio >= 1.2) return 0.9;     // Well staffed
    
    return 1.0;                        // Adequately staffed
  }

  /**
   * Calculate incidents multiplier
   */
  calculateIncidentsMultiplier(incidents) {
    const totalIncidents = 
      (incidents.hai_cases || 0) + 
      (incidents.near_misses || 0) + 
      (incidents.compliance_failures || 0);
    
    if (totalIncidents >= 5) return 1.8;
    if (totalIncidents >= 3) return 1.5;
    if (totalIncidents >= 1) return 1.2;
    
    return 1.0;
  }

  /**
   * Calculate device risk multiplier
   */
  calculateDeviceMultiplier(devices) {
    const devicesPerBed = devices.invasive_devices_per_bed || 0;
    
    if (devicesPerBed >= 2.0) return 1.5;
    if (devicesPerBed >= 1.5) return 1.3;
    if (devicesPerBed >= 1.0) return 1.2;
    
    return 1.0;
  }

  /**
   * Determine risk level from multiplier
   */
  determineRiskLevel(multiplier) {
    if (multiplier >= 2.5) {
      return {
        level: 'Critical',
        rationale: 'Multiple high-risk factors present, immediate attention required'
      };
    }
    if (multiplier >= 1.8) {
      return {
        level: 'High',
        rationale: 'Elevated risk factors require enhanced precautions'
      };
    }
    if (multiplier >= 1.3) {
      return {
        level: 'Medium',
        rationale: 'Moderate risk factors present, standard precautions sufficient'
      };
    }
    return {
      level: 'Low',
      rationale: 'Minimal risk factors, routine infection control measures adequate'
    };
  }

  /**
   * Generate recommendations based on risk factors
   */
  generateRecommendations(multiplier, multipliers) {
    const recommendations = [];
    
    if (multipliers.staffing > 1.3) {
      recommendations.push('Consider requesting additional staff support');
    }
    if (multipliers.historical > 1.3) {
      recommendations.push('Review infection prevention protocols - elevated historical infection rate');
    }
    if (multipliers.patient > 1.5) {
      recommendations.push('Implement enhanced precautions for high-risk patient population');
    }
    if (multipliers.environmental > 1.2) {
      recommendations.push('Enhance environmental cleaning and ventilation protocols');
    }
    if (multipliers.devices > 1.3) {
      recommendations.push('Review device necessity and minimize device days when possible');
    }
    if (multipliers.incidents > 1.3) {
      recommendations.push('Conduct root cause analysis for recent incidents');
    }
    
    if (recommendations.length === 0) {
      recommendations.push('Continue routine infection control measures');
    }
    
    return recommendations;
  }

  /**
   * Get location name
   * Supports all location types: room, department, floor, building, location
   */
  async getLocationName(locationId, locationType) {
    try {
      if (locationType === 'room' || locationType === 'rooms') {
        try {
          const room = await roomService.getById(locationId);
          return room?.name || `Room ${locationId}`;
        } catch (error) {
          console.warn(`Room not found: ${locationId}`, error);
          return `Room ${locationId}`;
        }
      } else if (locationType === 'department' || locationType === 'departments') {
        try {
          const dept = await departmentService.getById(locationId);
          return dept?.name || `Department ${locationId}`;
        } catch (error) {
          console.warn(`Department not found: ${locationId}`, error);
          return `Department ${locationId}`;
        }
      } else if (locationType === 'floor' || locationType === 'floors') {
        try {
          const floor = await floorService.getById(locationId);
          return floor?.name || `Floor ${locationId}`;
        } catch (error) {
          console.warn(`Floor not found: ${locationId}`, error);
          return `Floor ${locationId}`;
        }
      } else if (locationType === 'building' || locationType === 'buildings') {
        try {
          const building = await buildingService.getById(locationId);
          return building?.name || `Building ${locationId}`;
        } catch (error) {
          console.warn(`Building not found: ${locationId}`, error);
          return `Building ${locationId}`;
        }
      } else if (locationType === 'location' || locationType === 'locations') {
        try {
          const location = await locationService.getById(locationId);
          return location?.name || `Location ${locationId}`;
        } catch (error) {
          console.warn(`Location not found: ${locationId}`, error);
          return `Location ${locationId}`;
        }
      } else if (locationType === 'organization' || locationType === 'organizations') {
        try {
          const organization = await organizationService.getById(locationId);
          return organization?.name || `Organization ${locationId}`;
        } catch (error) {
          console.warn(`Organization not found: ${locationId}`, error);
          return `Organization ${locationId}`;
        }
      }
      return `Location ${locationId}`;
    } catch (error) {
      console.error('Error getting location name:', error);
      return `Location ${locationId}`;
    }
  }

  /**
   * Aggregate risk from child locations (hierarchical calculation)
   * For floor: aggregates from departments
   * For building: aggregates from floors (which aggregate from departments)
   * For location: aggregates from buildings (which aggregate from floors)
   * For organization: aggregates from locations (which aggregate from buildings)
   * 
   * @param {string} locationId - Parent location ID
   * @param {string} locationType - "floor" | "building" | "location" | "organization"
   * @returns {Promise<Object>} Aggregated risk profile
   */
  async aggregateRiskFromChildren(locationId, locationType) {
    try {
      // Normalize location type
      const normalizedType = locationType === 'floors' ? 'floor' : 
                             locationType === 'buildings' ? 'building' : 
                             locationType === 'locations' ? 'location' : 
                             locationType === 'organizations' ? 'organization' : locationType;
      
      let childLocations = [];
      let childType = '';
      
      // Get child locations based on parent type
      if (normalizedType === 'floor') {
        // Floor aggregates from departments
        childLocations = await departmentService.getWhere('floorId', '==', locationId);
        childType = 'department';
      } else if (normalizedType === 'building') {
        // Building aggregates from floors
        childLocations = await floorService.getWhere('buildingId', '==', locationId);
        childType = 'floor';
      } else if (normalizedType === 'location') {
        // Location aggregates from buildings
        childLocations = await buildingService.getWhere('locationId', '==', locationId);
        childType = 'building';
      } else if (normalizedType === 'organization') {
        // Organization aggregates from locations
        childLocations = await locationService.getWhere('organizationId', '==', locationId);
        childType = 'location';
      } else {
        throw new Error(`Invalid location type for aggregation: ${normalizedType}`);
      }
      
      if (childLocations.length === 0) {
        // No child locations, return default risk
        const locationName = await this.getLocationName(locationId, normalizedType);
        return {
          location_id: locationId,
          location_type: normalizedType,
          location_name: locationName,
          location_risk_multiplier: 1.0,
          risk_level: 'Low',
          risk_level_rationale: 'No child locations found',
          risk_factors: {},
          multipliers: {},
          recommendations: ['Add child locations to enable risk calculation'],
          is_aggregated: true,
          child_count: 0
        };
      }
      
      // Calculate or get risk profiles for all child locations
      const childRiskPromises = childLocations.map(async (child) => {
        // Try to get existing profile first
        let childProfile = await this.getLocationRiskProfile(child.id, childType);
        
        // If no profile exists or is outdated, calculate it
        if (!childProfile) {
          childProfile = await this.calculateLocationRisk(child.id, childType);
        } else {
          // Check if profile is outdated (older than 1 hour)
          const lastCalculated = childProfile.last_calculated?.toDate?.() || new Date(0);
          const hoursSinceCalculation = (Date.now() - lastCalculated.getTime()) / (1000 * 60 * 60);
          
          if (hoursSinceCalculation > 1) {
            // Recalculate if outdated
            childProfile = await this.calculateLocationRisk(child.id, childType);
          }
        }
        
        return {
          child_id: child.id,
          child_name: child.name || child.id,
          risk_multiplier: childProfile.location_risk_multiplier || 1.0,
          patient_count: childProfile.risk_factors?.current_patient_risk?.total_patients || 0,
          bed_count: childProfile.risk_factors?.device_risk?.total_beds || 0
        };
      });
      
      const childRisks = await Promise.all(childRiskPromises);
      
      // Calculate weighted average based on patient count or bed count
      let totalWeight = 0;
      let weightedSum = 0;
      
      childRisks.forEach(child => {
        // Use patient count as weight, fallback to bed count, then 1 if both are 0
        const weight = child.patient_count > 0 ? child.patient_count : 
                      (child.bed_count > 0 ? child.bed_count : 1);
        totalWeight += weight;
        weightedSum += child.risk_multiplier * weight;
      });
      
      const aggregatedMultiplier = totalWeight > 0 ? weightedSum / totalWeight : 1.0;
      const cappedMultiplier = Math.min(aggregatedMultiplier, 3.0);
      
      // Determine risk level
      const riskLevel = this.determineRiskLevel(cappedMultiplier);
      
      // Get location name
      const locationName = await this.getLocationName(locationId, normalizedType);
      
      // Aggregate risk factors from children (weighted averages)
      const aggregatedFactors = this.aggregateRiskFactors(childRisks, childLocations);
      
      // Generate recommendations
      const recommendations = this.generateAggregatedRecommendations(cappedMultiplier, childRisks);
      
      // Build aggregated risk profile
      const riskProfile = {
        location_id: locationId,
        location_type: normalizedType,
        location_name: locationName,
        
        // Base risk
        base_risk_score: cappedMultiplier,
        
        // Aggregated multipliers
        multipliers: aggregatedFactors.multipliers || {},
        
        // Risk factors with multipliers
        risk_factors: aggregatedFactors.risk_factors || {},
        
        // Calculated multiplier
        location_risk_multiplier: cappedMultiplier,
        
        // Risk classification
        risk_level: riskLevel.level,
        risk_level_rationale: riskLevel.rationale,
        
        // Biological entities (aggregated from children)
        endemic_organisms: aggregatedFactors.endemic_organisms || [],
        
        // Mitigation
        active_interventions: [],
        recommended_precautions: recommendations,
        
        // Metadata
        is_aggregated: true,
        child_count: childLocations.length,
        child_locations: childRisks.map(c => ({
          id: c.child_id,
          name: c.child_name,
          risk_multiplier: c.risk_multiplier
        })),
        last_calculated: serverTimestamp(),
        calculation_frequency: 'hourly',
        next_calculation: new Date(Date.now() + 60 * 60 * 1000), // 1 hour
        updated_at: serverTimestamp()
      };
      
      // Save to database
      await this.saveLocationRiskProfile(locationId, riskProfile);
      
      return {
        location_id: locationId,
        location_type: normalizedType,
        location_name: locationName,
        location_risk_multiplier: cappedMultiplier,
        risk_level: riskLevel.level,
        risk_level_rationale: riskLevel.rationale,
        risk_factors: riskProfile.risk_factors,
        multipliers: aggregatedFactors.multipliers || {},
        recommendations: recommendations,
        is_aggregated: true,
        child_count: childLocations.length
      };
    } catch (error) {
      console.error('Error aggregating risk from children:', error);
      throw error;
    }
  }

  /**
   * Aggregate risk factors from child locations
   */
  aggregateRiskFactors(childRisks, childLocations) {
    // This is a simplified aggregation - in production, you'd want more sophisticated weighting
    const multipliers = {
      historical: 1.0,
      patient: 1.0,
      environmental: 1.0,
      staffing: 1.0,
      incidents: 1.0,
      devices: 1.0
    };
    
    // Calculate average multipliers (simplified - could be weighted)
    const avgMultiplier = childRisks.reduce((sum, child) => sum + child.risk_multiplier, 0) / childRisks.length;
    
    return {
      multipliers: multipliers,
      risk_factors: {},
      endemic_organisms: []
    };
  }

  /**
   * Generate recommendations for aggregated locations
   */
  generateAggregatedRecommendations(multiplier, childRisks) {
    const recommendations = [];
    
    // Find highest risk children
    const highRiskChildren = childRisks.filter(c => c.risk_multiplier >= 1.8);
    if (highRiskChildren.length > 0) {
      recommendations.push(`Focus on ${highRiskChildren.length} high-risk child location(s): ${highRiskChildren.map(c => c.child_name).join(', ')}`);
    }
    
    if (multiplier >= 1.8) {
      recommendations.push('Overall elevated risk - review all child locations');
    }
    
    if (recommendations.length === 0) {
      recommendations.push('Continue routine infection control measures across all child locations');
    }
    
    return recommendations;
  }

  /**
   * Get endemic organisms (placeholder)
   */
  async getEndemicOrganisms(locationId, locationType) {
    // TODO: Query from HAI data, biological entity tracking
    return [];
  }

  /**
   * Save location risk profile to Firestore
   */
  async saveLocationRiskProfile(locationId, riskProfile) {
    try {
      // Check if profile exists
      const existingQuery = query(
        this.profilesCollection,
        where('location_id', '==', locationId),
        where('location_type', '==', riskProfile.location_type)
      );
      const existingDocs = await getDocs(existingQuery);
      
      if (!existingDocs.empty) {
        // Update existing
        const docId = existingDocs.docs[0].id;
        await updateDoc(doc(this.profilesCollection, docId), {
          ...riskProfile,
          updated_at: serverTimestamp()
        });
        return docId;
      } else {
        // Create new
        const docRef = await addDoc(this.profilesCollection, {
          ...riskProfile,
          created_at: serverTimestamp()
        });
        return docRef.id;
      }
    } catch (error) {
      console.error('Error saving location risk profile:', error);
      throw error;
    }
  }

  /**
   * Get location risk profile
   */
  async getLocationRiskProfile(locationId, locationType = 'room') {
    try {
      const q = query(
        this.profilesCollection,
        where('location_id', '==', locationId),
        where('location_type', '==', locationType)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      
      return null;
    } catch (error) {
      console.error('Error getting location risk profile:', error);
      throw error;
    }
  }

  /**
   * Get location risk multiplier (quick access)
   */
  async getLocationMultiplier(locationId, locationType = 'room') {
    const profile = await this.getLocationRiskProfile(locationId, locationType);
    if (profile) {
      return profile.location_risk_multiplier || 1.0;
    }
    
    // If no profile exists, calculate on the fly
    const calculation = await this.calculateLocationRisk(locationId, locationType);
    return calculation.location_risk_multiplier;
  }

  /**
   * Get all location risk profiles for a department/building
   */
  async getAllLocationRiskProfiles(locationType, parentId = null) {
    try {
      let q = query(
        this.profilesCollection,
        where('location_type', '==', locationType),
        orderBy('location_risk_multiplier', 'desc')
      );
      
      if (parentId) {
        // Filter by parent location if needed
        // This would require additional fields in the profile
      }
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting all location risk profiles:', error);
      throw error;
    }
  }
}

export const locationRiskService = new LocationRiskService();
